<template>
  <ModuleContainer>
    <DraggablePopup
      :top="popUpPosition.top"
      :right="popUpPosition.right"
      :left="popUpPosition.left"
      :bottom="popUpPosition.bottom"
      :title="title"
      :visible.sync="popupVisible"
      width="20em"
      height="410px"
      collapsable
      closable
    >
      <div class="fill-height px-2 py-1">
        <!-- Header -->

        <!-- Content -->
        <div class="default-overflow px-4 py-2">
          <v-text-field label="Title" v-model="form.title" />

          <v-select
            v-model="form.type"
            :items="Object.values(PRINT_TYPE)"
            label="Export As"
          />

          <template v-if="form.type === PRINT_TYPE.PDF">
            <v-select
              v-model="form.pageOrientation"
              :items="Object.values(PAGE_ORIENTATION)"
              label="Page Orientation"
            />

            <v-select
              v-model="form.pageSize"
              :items="Object.values(PAGE_SIZES)"
              label="Page Size"
            />
          </template>

          <v-checkbox v-model="form.legends" label="Legends" />

          <div
            class="px-2 py-1"
            style="position: absolute; bottom: 0; right: 0"
          >
            <v-btn :disabled="exportLoading" @click="popupVisible = false"
              >Cancel</v-btn
            >

            <v-btn :loading="exportLoading" color="primary" @click="exportMap"
              >Export</v-btn
            >
          </div>
        </div>
      </div>
    </DraggablePopup>

    <slot />
  </ModuleContainer>
</template>

<script>
// import L from '@plugins/leaflet'
import ModuleMixin from "@/components/Map/ModuleMixin";

import DraggablePopup from "@/components/DraggablePopup/DraggablePopup.vue";

import { ButtonGroupControl } from "@/components/Map/leaflet/ButtonGroupControl";

import moment from "moment";
import pdfMake from "@/libs/generate-report/pdfmake/index";
import standardPageSizes from "pdfmake/src/standardPageSizes";

import { MapScreenShotPrinter } from "./MapScreenShotPrinter";
import { MapGraticuleDrawer } from "./MapGraticuleDrawer";
import { MapCompassRoseCreator } from "./MapCompassRoseCreator";
// import { MapScaleBarDrawer } from "./MapScaleBarDrawer";
import { LogoDrawer } from "./LogoDrawer";
import { saveFile } from "@/utils/FileSaver";
import {
  clearBackground,
  drawImage,
  drawText,
  measureWrapTextHeight,
  drawWrapText
} from "@/utils/Canvas";

/**
 * @enum {String}
 */
export const PRINT_TYPE = {
  PNG: "PNG",
  PDF: "PDF"
};

/**
 * @enum {String}
 */
export const PAGE_SIZES = {
  A0: "A0",
  A1: "A1",
  A3: "A3",
  A4: "A4"
};

/**
 * @enum {String}
 */
export const PAGE_ORIENTATION = {
  PORTRAIT: "portrait",
  LANDSCAPE: "landscape"
};

export default {
  mixins: [ModuleMixin],

  components: {
    DraggablePopup
  },

  props: {
    title: {
      type: String,
      default: "Print Map"
    },

    popUpPosition: {
      type: Object,
      default: () => ({
        top: 10,
        right: 40
      })
    }
  },

  inject: ["printLegends"],

  data() {
    return {
      popupVisible: false,

      exportLoading: false,
      form: {
        title: "",
        type: PRINT_TYPE.PNG,
        pageOrientation: PAGE_ORIENTATION.LANDSCAPE,
        pageSize: PAGE_SIZES.A4,
        legends: true
      }
    };
  },

  computed: {
    PRINT_TYPE() {
      return PRINT_TYPE;
    },

    PAGE_ORIENTATION() {
      return PAGE_ORIENTATION;
    },

    PAGE_SIZES() {
      return PAGE_SIZES;
    }
  },

  methods: {
    onInit() {
      // Use this.map to access leaflet map
      this.addControl(
        ButtonGroupControl.create([
          {
            title: this.title,
            icon: "print",
            onClick: () => {
              this.popupVisible = !this.popupVisible;
            }
          }
        ])
      );
    },

    onDestroy() {},

    async exportMap() {
      try {
        this.exportLoading = true;

        const map = await this.printMap(this.form);

        switch (this.form.type) {
          case PRINT_TYPE.PNG:
            this.savePng(map);
            break;
          case PRINT_TYPE.PDF:
            this.savePdf(map, this.form);
            break;
        }

        this.popupVisible = false;
      } finally {
        this.exportLoading = false;
      }
    },

    async printMap({ title, legends }) {
      const BASE_MARGIN = 10;

      const mapCanvas = await MapScreenShotPrinter.print(this.map);

      const compassRoseImage = await MapCompassRoseCreator.create();

      const logoCanvas = await LogoDrawer.create();

      const graticuleMapCanvas = MapGraticuleDrawer.draw(mapCanvas, this.map);

      // const isMobile = screen.width <= 600;

      // const scaleBarCanvas = MapScaleBarDrawer.draw(this.map, {
      //   maxWidth: isMobile ? 200 : 300
      // }).canvas;

      const legendsCanvas = legends
        ? await this.printLegends()
        : document.createElement("canvas");

      // const disclaimerText =
      // "Disclaimer: District and IMC ward boundaries are depicted as notified by the boundary delimitation committee notification issued by the concerned department and general administration department from time to time. Disputes if any arises SRSAC, Arunachal Pradesh shall not be the sole authority to resolve boundary disputes and it lies with the concerned authority only.";
      const disclaimerText = "";
      const disclaimerHeight = measureWrapTextHeight(
        "18px serif",
        disclaimerText,
        graticuleMapCanvas.width
      );

      // Create Final Map Canvas with width & height before drawing
      const finalMap = document.createElement("canvas");

      finalMap.width =
        BASE_MARGIN +
        graticuleMapCanvas.width +
        BASE_MARGIN +
        legendsCanvas.width +
        BASE_MARGIN;

      const headerHeight = Math.max(compassRoseImage.height, logoCanvas.height);

      const contentHeight = Math.max(
        graticuleMapCanvas.height + BASE_MARGIN + disclaimerHeight,
        legendsCanvas.height
      );

      finalMap.height =
        BASE_MARGIN +
        headerHeight +
        BASE_MARGIN +
        contentHeight +
        // BASE_MARGIN +
        // Math.max(legendsCanvas.height, scaleBarCanvas.height) +
        BASE_MARGIN;

      // Clear background
      clearBackground(finalMap);

      // Draw Logo
      drawImage(finalMap, logoCanvas, {
        top: BASE_MARGIN,
        left: BASE_MARGIN
      });

      // Draw compass
      drawImage(finalMap, compassRoseImage, {
        top: BASE_MARGIN,
        right: BASE_MARGIN
      });

      // Draw Title Text
      drawText(
        finalMap,
        title || `Map ${moment().format("YYYY-MM-DD HH:mm:ss")}`,
        {
          x: finalMap.width / 2,
          y: BASE_MARGIN + headerHeight / 2
        },
        {
          font: "bold 32px serif"
        }
      );

      // Draw Map
      drawImage(finalMap, graticuleMapCanvas, {
        left: BASE_MARGIN,
        top: BASE_MARGIN + headerHeight + BASE_MARGIN
      });

      // Draw Disclaimer
      drawWrapText(
        finalMap,
        disclaimerText,
        BASE_MARGIN,
        BASE_MARGIN +
          headerHeight +
          BASE_MARGIN +
          graticuleMapCanvas.height +
          BASE_MARGIN,
        graticuleMapCanvas.width,
        {
          font: "18px serif"
        }
      );

      // Draw Legends
      drawImage(finalMap, legendsCanvas, {
        right: BASE_MARGIN,
        top: BASE_MARGIN + headerHeight + BASE_MARGIN
      });

      // Draw scale bar
      // drawImage(finalMap, scaleBarCanvas, {
      //   top:
      //     BASE_MARGIN +
      //     headerHeight +
      //     BASE_MARGIN +
      //     graticuleMapCanvas.height +
      //     BASE_MARGIN +
      //     disclaimerHeight +
      //     BASE_MARGIN,
      //   right: BASE_MARGIN
      // });

      return finalMap;
    },

    savePng(map) {
      saveFile(map, `map-${moment().format("YYYYMMDD-HHmmss")}`);
    },

    savePdf(map, options = {}) {
      const pdfOptions = {
        pageOrientation: "portrait",
        pageSize: "A4",
        pageMargins: [0, 0, 0, 0],
        ...options
      };

      const pageSizeInPixels = standardPageSizes[pdfOptions.pageSize];

      pdfMake
        .createPdf({
          ...pdfOptions,
          content: [
            {
              image: map.toDataURL(),
              fit:
                pdfOptions.pageOrientation === PAGE_ORIENTATION.LANDSCAPE
                  ? [pageSizeInPixels[1], pageSizeInPixels[0]]
                  : pageSizeInPixels,
              style: "alignCenter"
            }
          ],
          styles: {
            alignCenter: {
              alignment: "center"
            }
          }
        })
        .download(`map-${moment().format("YYYYMMDD-HHmmss")}`);
    }
  }
};
</script>
